import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useHistory } from 'react-router';
import { keyframes } from '@emotion/react';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-14px) rotate(-6deg); }
`;

const blobDrift = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(24px, -18px) scale(1.08); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

// หน้า 404 กลาง — ใช้ทั้งเป็น catch-all นอกสุด (index.js, URL ที่ไม่ตรง route ไหนเลยแม้แต่ prefix)
// และเป็น fallback ในสุดของแต่ละ layout module (Project.js/Finance.js/ฯลฯ, URL ที่ตรง prefix โมดูล
// แต่ sub-path ข้างในไม่ตรงอันไหนเลย) — ไม่รับ prop ใดๆ ทั้งสองบริบทเรียก <NotFound /> เหมือนกัน
// ต้อง component เดียวกันได้ทั้งคู่ ไม่ต้องแยกไฟล์
export default function NotFound() {
  const history = useHistory();

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        px: 2,
        bgcolor: 'background.default',
      }}
    >
      {/* บล็อบไล่สีเบลอลอยเบาๆ ด้านหลัง — โทนเดียวกับ primary/secondary ของธีม */}
      <Box
        sx={{
          position: 'absolute',
          top: '12%',
          left: '10%',
          width: 260,
          height: 260,
          borderRadius: '50%',
          background: (theme) => `radial-gradient(circle, ${theme.palette.primary.light} 0%, transparent 70%)`,
          opacity: 0.35,
          filter: 'blur(10px)',
          animation: `${blobDrift} 9s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '12%',
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: (theme) => `radial-gradient(circle, ${theme.palette.secondary.light} 0%, transparent 70%)`,
          opacity: 0.3,
          filter: 'blur(10px)',
          animation: `${blobDrift} 11s ease-in-out infinite reverse`,
        }}
      />

      <Box
        sx={{
          position: 'relative',
          textAlign: 'center',
          maxWidth: 460,
          animation: `${fadeUp} 0.5s ease-out`,
        }}
      >
        <Box
          sx={{
            width: 96,
            height: 96,
            mx: 'auto',
            mb: 2,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            boxShadow: (theme) => `0 16px 32px -12px ${theme.palette.primary.main}66`,
            animation: `${float} 3.5s ease-in-out infinite`,
          }}
        >
          <TravelExploreOutlinedIcon sx={{ fontSize: 46, color: '#fff' }} />
        </Box>

        <Typography
          sx={{
            fontSize: { xs: '4rem', sm: '5.5rem' },
            fontWeight: 800,
            lineHeight: 1,
            backgroundImage: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            letterSpacing: 2,
          }}
        >
          404
        </Typography>

        <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
          ไม่พบหน้าที่คุณต้องการ
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 3.5 }}>
          ลิงก์นี้อาจไม่ถูกต้อง หรือหน้านี้อาจถูกย้าย/ลบไปแล้ว
        </Typography>

        <Button
          variant="contained"
          size="large"
          startIcon={<HomeOutlinedIcon />}
          onClick={() => history.push('/')}
          sx={{
            borderRadius: 2.5,
            px: 4,
            boxShadow: (theme) => `0 10px 24px -8px ${theme.palette.primary.main}80`,
          }}
        >
          กลับสู่หน้าหลัก
        </Button>
      </Box>
    </Box>
  );
}
